import { useAuth } from "../context/AuthContext";
import { useEffect, useState, useRef } from "react";
import { Search, Plus, Package, FileText, Edit2, Trash2, AlertTriangle, ChevronLeft, ChevronRight, Layers, Upload, Download } from "lucide-react";
import ProductForm from "../components/ProductForm";
import api from "../services/api";
import Swal from "sweetalert2";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);
  
  // 🆕 NUEVO: Totales desde el backend
  const [stockTotal, setStockTotal] = useState(0);
  const [stockBajo, setStockBajo] = useState(0);

  // 🆕 Import Excel
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const { isEncargado } = useAuth();

  // 🆕 Export Excel
  const [exportando, setExportando] = useState(false);

  const fetchProducts = async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      const res = await api.get(`/products/paginated?page=${page}&search=${searchTerm}`);
      
      if (res.data.data) {
        setProducts(res.data.data);
        setCurrentPage(res.data.current_page);
        setTotalPages(res.data.last_page);
        setTotal(res.data.total);
        setFrom(res.data.from);
        setTo(res.data.to);
        
        // 🆕 NUEVO: Guardar totales desde el backend
        setStockTotal(res.data.stock_total || 0);
        setStockBajo(res.data.stock_bajo || 0);
      } else {
        setProducts(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error("Error cargando productos:", error);
      Swal.fire("Error", "No se pudieron cargar los productos", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();

    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchProducts(newPage, search);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    
    clearTimeout(window.searchTimeout);
    
    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts(1, value);
    }, 500);
  };

  const handleDelete = async (product) => {
    const tipoTexto = product.tipo_item === 2 ? "servicio" : "producto";
    
    const res = await Swal.fire({
      title: `¿Eliminar ${tipoTexto}?`,
      text: `Se eliminará: ${product.nombre}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (res.isConfirmed) {
      try {
        await api.delete(`/products/${product.id}`);
        Swal.fire("Eliminado", `${tipoTexto.charAt(0).toUpperCase() + tipoTexto.slice(1)} eliminado correctamente`, "success");
        fetchProducts(currentPage, search);
      } catch (error) {
        Swal.fire("Error", `No se pudo eliminar el ${tipoTexto}`, "error");
      }
    }
  };

  // 🆕 Descargar plantilla CSV
  const descargarPlantilla = async () => {
    try {
      const response = await api.get('/products/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_productos.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      Swal.fire('Error', 'No se pudo descargar la plantilla', 'error');
    }
  };

  // 🆕 Importar archivo
  const handleImport = async () => {
    if (!importFile) {
      Swal.fire('Atención', 'Selecciona un archivo primero', 'warning');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post('/products/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      fetchProducts(1, '');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Error al importar el archivo', 'error');
    } finally {
      setImporting(false);
    }
  };

  const cerrarImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

    // 🆕 Exportar inventario a Excel
  const exportarInventario = async () => {
    setExportando(true);
    try {
      const response = await api.get('/products/export', { responseType: 'blob' });

      // Nombre del archivo que manda el backend (o uno por defecto)
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const hoy = new Date().toISOString().slice(0, 10);
      const nombreArchivo = match ? match[1] : `Inventario_${hoy}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Como pedimos blob, el mensaje de error viene dentro del blob
      let mensaje = 'No se pudo exportar el inventario';
      try {
        const texto = await error.response?.data?.text();
        const json = JSON.parse(texto);
        if (json.message) mensaje = json.message;
      } catch (e) {}
      Swal.fire('Error', mensaje, 'error');
    } finally {
      setExportando(false);
    }
  };

  // ✅ FUNCIÓN ACTUALIZADA: Mostrar presentaciones en modal con stock e información completa
  // 🆕 NUEVA FUNCIÓN: Mostrar productos con stock bajo
  const mostrarProductosStockBajo = async () => {
    try {
      const res = await api.get('/products/stock-bajo'); // o '/products/stockBajo' según tu ruta
      const productosStockBajo = res.data;

      if (productosStockBajo.length === 0) {
        Swal.fire({
          icon: 'success',
          title: '✅ Todo bien',
          text: 'No hay productos con stock bajo',
          confirmButtonText: 'Entendido'
        });
        return;
      }

      // Crear HTML para mostrar los productos
      const productosHTML = productosStockBajo
        .map((p) => {
          const stockBajo = p.stock <= (p.stock_minimo || 5);
          return `
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 16px;
              background: ${stockBajo ? '#fff5f5' : '#f7fafc'};
              border-radius: 8px;
              margin-bottom: 8px;
              border: 2px solid ${stockBajo ? '#fc8181' : '#e2e8f0'};
            ">
              <div style="text-align: left; flex: 1;">
                <div style="font-weight: 600; color: #2d3748; font-size: 15px;">
                  ${p.nombre}
                </div>
                <div style="font-size: 12px; color: #718096; margin-top: 2px;">
                  Código: ${p.codigo || 'N/A'} | Mínimo: ${p.stock_minimo || 5}
                </div>
              </div>
              <div style="text-align: right;">
                <div style="font-weight: 700; color: #ed8936; font-size: 18px;">
                  ⚠️ ${p.stock}
                </div>
                <div style="font-size: 11px; color: #718096;">
                  unidades
                </div>
              </div>
            </div>
          `;
        })
        .join("");

      Swal.fire({
        title: `⚠️ Productos con Stock Bajo (${productosStockBajo.length})`,
        html: `
          <div style="text-align: left; max-height: 400px; overflow-y: auto;">
            <p style="color: #718096; font-size: 14px; margin-bottom: 16px;">
              Estos productos necesitan reposición:
            </p>
            ${productosHTML}
          </div>
        `,
        confirmButtonText: "Cerrar",
        width: "600px",
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    } catch (error) {
      console.error("Error cargando productos con stock bajo:", error);
      Swal.fire("Error", "No se pudieron cargar los productos", "error");
    }
  };

  const mostrarPresentaciones = async (producto) => {
    try {
      // Obtener el producto con sus presentaciones
      const res = await api.get(`/products/${producto.id}`);
      const productData = res.data;

      if (!productData.presentations || productData.presentations.length === 0) {
        Swal.fire("Sin presentaciones", "Este producto no tiene presentaciones configuradas.", "info");
        return;
      }

      // Crear HTML para mostrar las presentaciones
      const presentacionesHTML = productData.presentations
        .map((pres) => {
          const tipoTexto = pres.tipo_presentacion === 'variante' ? 'Variante' : 'Empaque';
          const tipoBadgeColor = pres.tipo_presentacion === 'variante' ? '#805ad5' : '#38b2ac';
          
          // Determinar stock a mostrar
          let stockHTML = '';
          if (pres.tipo_presentacion === 'variante') {
            const stock = pres.stock_independiente || 0;
            const stockBajo = stock <= 5;
            stockHTML = `
              <div style="font-size: 12px; color: ${stockBajo ? '#ed8936' : '#718096'}; margin-top: 4px;">
                ${stockBajo ? '⚠️' : '📦'} Stock: <strong>${stock}</strong>
              </div>
            `;
          } else {
            stockHTML = `
              <div style="font-size: 12px; color: #718096; margin-top: 4px;">
                📦 Factor: ${pres.factor_conversion} unidad${pres.factor_conversion > 1 ? 'es' : ''}
              </div>
            `;
          }
          
          // Precio de compra (solo para variantes)
          const precioCompraHTML = pres.tipo_presentacion === 'variante' && pres.precio_compra
            ? `<div style="font-size: 13px; color: #718096; margin-top: 2px;">Costo: $${Number(pres.precio_compra).toFixed(2)}</div>`
            : '';

          return `
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 16px;
              background: #f7fafc;
              border-radius: 8px;
              margin-bottom: 8px;
              border: 2px solid #e2e8f0;
            ">
              <div style="text-align: left; flex: 1;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                  <div style="font-weight: 600; color: #2d3748; font-size: 15px;">
                    ${pres.descripcion}
                  </div>
                  <span style="
                    font-size: 10px;
                    padding: 2px 6px;
                    background: ${tipoBadgeColor};
                    color: white;
                    border-radius: 4px;
                    font-weight: 600;
                  ">
                    ${tipoTexto}
                  </span>
                </div>
                ${stockHTML}
                ${precioCompraHTML}
              </div>
              <div style="text-align: right;">
                <div style="font-weight: 700; color: #48bb78; font-size: 18px;">
                  $${Number(pres.precio_venta).toFixed(2)}
                </div>
                <div style="font-size: 11px; color: #718096;">
                  Precio Venta
                </div>
              </div>
            </div>
          `;
        })
        .join("");

      Swal.fire({
        title: `📦 ${producto.nombre}`,
        html: `
          <div style="text-align: left; max-height: 400px; overflow-y: auto;">
            <p style="color: #718096; font-size: 14px; margin-bottom: 16px;">
              Presentaciones disponibles:
            </p>
            ${presentacionesHTML}
          </div>
        `,
        confirmButtonText: "Cerrar",
        width: "600px",
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    } catch (error) {
      console.error("Error cargando presentaciones:", error);
      Swal.fire("Error", "No se pudieron cargar las presentaciones", "error");
    }
  };

  // ✅ FUNCIÓN: Mostrar stock (oculta solo para presentaciones VARIANTE)
  const mostrarStock = (producto) => {
    if (producto.tipo_item === 2) {
      return <span style={{ color: "#a0aec0" }}>-</span>;
    }

    // 🆕 Solo ocultar stock si tiene presentaciones tipo VARIANTE
    if (producto.tiene_presentaciones_variante) {
      return <span style={{ color: "#a0aec0" }}>-</span>;
    }

    // Mostrar stock normal para:
    // - Productos sin presentaciones
    // - Productos con presentaciones EMPAQUE (stock compartido)
    const stockBajo = producto.stock <= (producto.stock_minimo || 5);
    
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontWeight: 600,
          color: stockBajo ? "#ed8936" : "#2d3748",
        }}
      >
        {stockBajo && <AlertTriangle size={16} color="#ed8936" />}
        {producto.stock}
      </span>
    );
  };

  // ✅ FUNCIÓN: Mostrar precio o botón "Ver presentaciones"
  const mostrarPrecio = (producto) => {
    const precio = Number(producto.precio_venta || 0);
    
    // Si el precio es 0, significa que tiene presentaciones
    if (precio === 0) {
      return (
        <button
          onClick={() => mostrarPresentaciones(producto)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "13px",
            padding: "6px 12px",
            background: "#e6fffa",
            color: "#38b2ac",
            border: "2px solid #38b2ac",
            borderRadius: "6px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#38b2ac";
            e.target.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#e6fffa";
            e.target.style.color = "#38b2ac";
          }}
          title="Ver precios de cada presentación"
        >
          <Layers size={14} />
          Ver presentaciones
        </button>
      );
    }

    // Si tiene precio, mostrarlo normal
    return (
      <span
        style={{
          fontWeight: 700,
          color: "#48bb78",
        }}
      >
        ${precio.toFixed(2)}
      </span>
    );
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <Package size={28} color="#667eea" />
            <h1 className="card-title">Inventario de Productos/Servicios</h1>
          </div>

          {!showForm && (
            <div className="d-flex gap-2">
              {/* 🆕 Botón Exportar Inventario */}
              <button
                className="btn btn-secondary"
                onClick={exportarInventario}
                disabled={exportando}
              >
                <Download size={18} style={{ marginRight: 6 }} />
                {exportando ? 'Exportando...' : 'Exportar Inventario'}
              </button>
              {/* 🆕 Botón Importar Excel */}
              {isEncargado && (<>
              <button
                className="btn btn-secondary"
                onClick={() => setShowImportModal(true)}
              >
                <Upload size={18} style={{ marginRight: 6 }} />
                Importar Excel
              </button>
              <button
                className="btn btn-success"
                onClick={() => {
                  setEditingProduct(null);
                  setShowForm(true);
                }}
              >
                <Plus size={18} />
                Nuevo Producto/Servicio
              </button>
                </>)}
            </div>
          )}
        </div>

        <div className="card-body">
          {showForm && (
            <ProductForm
              editingProduct={editingProduct}
              onSaved={() => {
                fetchProducts(currentPage, search);
                setEditingProduct(null);
                setShowForm(false);
              }}
              onCancel={() => {
                setEditingProduct(null);
                setShowForm(false);
                setTimeout(() => searchRef.current?.focus(), 50);
              }}
            />
          )}

          {!showForm && (
            <div className="form-group" style={{ position: "relative" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a0aec0",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre o código..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchRef}
                  style={{ paddingLeft: "44px" }}
                />
              </div>
              {search && (
                <small style={{ color: "#718096", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  🔍 Buscando en todos los productos/servicios...
                </small>
              )}
            </div>
          )}

          {!showForm && (
            <>
              {loading ? (
                <div className="text-center" style={{ padding: "40px" }}>
                  <div className="spinner" style={{ margin: "0 auto" }}></div>
                  <p style={{ marginTop: "16px", color: "#718096" }}>
                    Cargando productos...
                  </p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>Foto</th>
                        <th>Código</th>
                        <th>Nombre</th>
                        <th>Descripción</th>
                        <th style={{ textAlign: "right" }}>Precio</th>
                        <th style={{ textAlign: "center" }}>Stock</th>
                        <th style={{ textAlign: "center", width: "180px" }}>
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length === 0 ? (
                        <tr>
                          <td colSpan="7">
                            <div
                              className="text-center"
                              style={{ padding: "40px 20px" }}
                            >
                              <FileText
                                size={48}
                                color="#cbd5e0"
                                style={{ marginBottom: "16px" }}
                              />
                              <p
                                style={{
                                  color: "#a0aec0",
                                  fontSize: "16px",
                                  margin: 0,
                                }}
                              >
                                {search
                                  ? "No se encontraron productos/servicios con ese criterio"
                                  : "No hay productos/servicios registrados"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                                                products.map((p) => (
                          <tr key={p.id}>
                            <td>
                              {p.imagen_url ? (
                                <img
                                  src={p.imagen_url}
                                  alt={p.nombre}
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    border: "1px solid #e2e8f0",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "6px",
                                    background: "#f7fafc",
                                    border: "1px solid #e2e8f0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Package size={18} color="#cbd5e0" />
                                </div>
                              )}
                            </td>
                            <td>
                              <strong style={{ fontWeight: 600 }}>
                                {p.codigo || "-"}
                              </strong>
                            </td>
                            <td>
                              {p.nombre}
                              {p.tipo_item === 2 && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                    background: "#e6fffa",
                                    color: "#38b2ac",
                                    borderRadius: "4px",
                                    fontWeight: 600,
                                  }}
                                >
                                  SERVICIO
                                </span>
                              )}
                            </td>
                            <td style={{ color: "#718096" }}>
                              {p.descripcion || "-"}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {mostrarPrecio(p)}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {mostrarStock(p)}
                            </td>
                            <td>
                              <div
                                className="d-flex gap-2"
                                style={{ justifyContent: "center" }}
                              >
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => {
                                    setEditingProduct(p);
                                    setShowForm(true);
                                  }}
                                  title="Editar producto/servicio"
                                >
                                  <Edit2 size={16} />
                                </button>
                                {isEncargado && (
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(p)}
                                  title="Eliminar producto/servicio"
                                >
                                  <Trash2 size={16} />
                                </button>
                                  )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>
                    Página {currentPage} de {totalPages}
                  </span>

                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {products.length > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f7fafc",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <span style={{ color: "#718096", fontSize: "14px" }}>
                    Mostrando {from} - {to} de {total} ítems
                  </span>
                  <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    <span style={{ color: "#718096", fontSize: "14px" }}>
                      Stock Total (Bienes):{" "}
                      <strong style={{ color: "#667eea" }}>
                        {stockTotal}
                      </strong>
                    </span>
                    <span 
                      style={{ 
                        color: "#718096", 
                        fontSize: "14px",
                        cursor: stockBajo > 0 ? "pointer" : "default"
                      }}
                      onClick={() => {
                        if (stockBajo > 0) {
                          mostrarProductosStockBajo();
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (stockBajo > 0) {
                          e.target.style.textDecoration = "underline";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = "none";
                      }}
                      title={stockBajo > 0 ? "Click para ver productos con stock bajo" : ""}
                    >
                      Stock Bajo:{" "}
                      <strong style={{ color: "#ed8936" }}>
                        {stockBajo}
                      </strong>
                      {stockBajo > 0 && " 👁️"}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 🆕 MODAL IMPORTAR EXCEL */}
      {showImportModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📥 Importar Productos desde Excel</h5>
                <button className="btn-close" onClick={cerrarImportModal} />
              </div>

              <div className="modal-body">
                {!importResult ? (
                  <>
                    {/* Descargar plantilla */}
                    <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                      <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>
                        <strong>¿Primera vez?</strong> Descarga la plantilla con el formato correcto.
                      </p>
                      <button className="btn btn-sm btn-success mt-2" onClick={descargarPlantilla}>
                        <Download size={14} style={{ marginRight: 4 }} /> Descargar Plantilla CSV
                      </button>
                    </div>

                    {/* Columnas requeridas */}
                    <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                      <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#1e40af' }}>Columnas del archivo:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {['nombre *', 'codigo', 'precio_venta *', 'precio_compra', 'stock', 'stock_minimo', 'descripcion', 'aplica_iva', 'tipo_item'].map(col => (
                          <span key={col} style={{ fontSize: 11, padding: '2px 8px', background: col.includes('*') ? '#dbeafe' : '#e0e7ff', color: '#1e40af', borderRadius: 4, fontWeight: col.includes('*') ? 700 : 400 }}>
                            {col}
                          </span>
                        ))}
                      </div>
                      <p style={{ margin: '8px 0 0', fontSize: 11, color: '#6b7280' }}>
                        * Obligatorios | tipo_item: 1=Bien, 2=Servicio | aplica_iva: 1=Sí, 0=No
                      </p>
                    </div>

                    {/* Selector de archivo */}
                    <div
                      style={{ border: '2px dashed #d1d5db', borderRadius: 8, padding: 24, textAlign: 'center', cursor: 'pointer', background: importFile ? '#f0fdf4' : '#fafafa' }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={32} color={importFile ? '#22c55e' : '#9ca3af'} style={{ marginBottom: 8 }} />
                      <p style={{ margin: 0, color: importFile ? '#16a34a' : '#6b7280', fontWeight: importFile ? 600 : 400 }}>
                        {importFile ? `✅ ${importFile.name}` : 'Click para seleccionar archivo'}
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>Excel (.xlsx, .xls) o CSV</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                        onChange={(e) => setImportFile(e.target.files[0] || null)}
                      />
                    </div>
                  </>
                ) : (
                  /* Resultado de importación */
                  <div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                      <div style={{ flex: 1, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#16a34a' }}>{importResult.importados}</div>
                        <div style={{ fontSize: 12, color: '#166534' }}>Importados</div>
                      </div>
                      <div style={{ flex: 1, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#2563eb' }}>{importResult.actualizados}</div>
                        <div style={{ fontSize: 12, color: '#1e40af' }}>Actualizados</div>
                      </div>
                      <div style={{ flex: 1, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>{importResult.errores}</div>
                        <div style={{ fontSize: 12, color: '#991b1b' }}>Errores</div>
                      </div>
                    </div>

                    {importResult.errores > 0 && (
                      <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #fca5a5', borderRadius: 8, padding: 8 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', margin: '0 0 8px' }}>Filas con error:</p>
                        {importResult.detalle.filter(d => d.estado === 'error').map((d, i) => (
                          <div key={i} style={{ fontSize: 12, color: '#6b7280', padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
                            <strong>Fila {d.fila}:</strong> {d.nombre} — {d.mensaje}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={cerrarImportModal}>
                  {importResult ? 'Cerrar' : 'Cancelar'}
                </button>
                {!importResult && (
                  <button className="btn btn-primary" onClick={handleImport} disabled={!importFile || importing}>
                    {importing
                      ? <><span className="spinner-border spinner-border-sm me-2" />Importando...</>
                      : '📥 Importar'
                    }
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}